/**
 * Commission Request Builder
 *
 * Handles building complete commission update requests from individual field changes.
 * When a user edits a commission field in the grid, we need to send ALL commission
 * values to the backend with proper validation based on policy period.
 */

import { logger } from "@platform/browser-logger";
import { NotDisclosed } from "@platform/openapi-types";

import { RfpCoverage } from "./types";
import { shouldOmitAmount } from "./commissionHelpers";

import type {
  GridCommission,
  HeapedCommissionValue,
} from "./commissionHelpers";

/**
 * Commission payload for API requests
 */
interface CommissionPayload {
  currentCommissionType?: string;
  currentCommissionAmount?: number | HeapedCommissionValue;
  requestedCommissionType?: string;
  requestedCommissionAmount?: number | HeapedCommissionValue;
}

/**
 * Commission types that require amount fields (not heaped)
 */
const TYPES_REQUIRING_AMOUNT = ["percentage", "pepm"];

/**
 * Gets the default amount for a commission type
 *
 * @param commissionType - The commission type
 * @returns Default amount (1 for percentage, 0 for pepm, undefined for others)
 */
const getDefaultAmount = (commissionType?: string): number | undefined => {
  if (!commissionType) return undefined;

  if (commissionType === "percentage") {
    return 1;
  }
  if (commissionType === "pepm") {
    return 0;
  }
  // For graded, heaped, net, standard, notApplicable - no default amount.
  // graded allows a blank amount ("no defined percentage").
  return undefined;
};

/**
 * Gets the default heaped amount for heaped commission type
 * Returns undefined to allow no percentages to be entered
 *
 * @param commissionType - The commission type
 * @returns undefined for heaped type (no default percentages required)
 */
const getDefaultHeapedAmount = (
  commissionType?: string,
): HeapedCommissionValue | undefined => {
  if (commissionType === "heaped") {
    return undefined; // No default - allow empty percentages
  }
  return undefined;
};

/**
 * Applies default amounts to commission data based on commission type
 *
 * Rules:
 * - If type is "percentage" and amount is undefined → default to 1
 * - If type is "pepm" and amount is undefined → default to 0
 * - If type is "graded" → no default (a blank amount is allowed)
 * - If type is "heaped" and heapedAmount is undefined → no default (allow empty)
 * - If type is "net", "standard", or "notApplicable" → no amount needed
 *
 * @param type - Commission type
 * @param amount - Current amount value
 * @returns Amount with default applied if needed
 */
const applyDefaultAmount = (
  type?: string,
  amount?: number | { first: number; thereafter: number },
): number | { first: number; thereafter: number } | undefined => {
  if (!type) return amount;

  // If type is heaped, handle heaped amount
  if (type === "heaped") {
    if (amount === undefined) {
      return getDefaultHeapedAmount(type);
    }
    return amount;
  }

  // If type requires an amount and amount is undefined, apply default
  if (TYPES_REQUIRING_AMOUNT.includes(type) && amount === undefined) {
    return getDefaultAmount(type);
  }

  // If type disables amount, return undefined (no amount)
  if (shouldOmitAmount(type)) {
    return undefined;
  }

  // Otherwise return the amount as-is
  return amount;
};

/**
 * Extracts commission field values from a benefit plan's benefits object
 *
 * @param benefitPlan - The benefit plan containing commission data
 * @returns Object with current and requested commission type and amount values
 */
const extractCommissionFieldsFromBenefitPlan = (benefitPlan: {
  benefits?: Partial<GridCommission>;
}): CommissionPayload => {
  const benefits = benefitPlan.benefits || {};

  return {
    currentCommissionType: benefits.currentCommissionType,
    currentCommissionAmount: benefits.currentCommissionAmount,
    requestedCommissionType: benefits.requestedCommissionType,
    requestedCommissionAmount: benefits.requestedCommissionAmount,
  };
};

/**
 * Applies a field change to commission data
 *
 * @param commissionData - Current commission field values
 * @param fieldChanged - The field name that was changed
 * @param newValue - The new value for the field
 * @returns Updated commission data with the changed field
 */
const applyFieldChange = (
  commissionData: CommissionPayload,
  fieldChanged: string,
  newValue: unknown,
): CommissionPayload => {
  const updated = {
    ...commissionData,
    [fieldChanged]: newValue,
  };

  // When commission type changes to "heaped" or "graded", clear amount so the
  // popover editor starts blank (no default percentage required for either).
  const clearsAmount = newValue === "heaped" || newValue === "graded";

  if (fieldChanged === "currentCommissionType" && clearsAmount) {
    updated.currentCommissionAmount = undefined;
  }

  if (fieldChanged === "requestedCommissionType" && clearsAmount) {
    updated.requestedCommissionAmount = undefined;
  }

  return updated;
};

/**
 * Builds commission update payload based on policy period rules
 *
 * - If policyPeriod is "current": Include both current and requested commission
 *   - currentType defaults to "notApplicable" if undefined (required by backend)
 * - Otherwise: Include only requested commission (current is not allowed)
 *
 * @param commissionData - All commission field values
 * @param policyPeriod - The benefit plan's policy period
 * @returns Commission update payload ready for API
 */
const buildCommissionPayload = (
  commissionData: CommissionPayload,
  policyPeriod: string,
): CommissionPayload => {
  const isCurrentPolicyPeriod = policyPeriod === "current";

  const requestedType = commissionData.requestedCommissionType ?? NotDisclosed;

  const payload: CommissionPayload = {
    // Always include requested commission (required by backend)
    requestedCommissionType: requestedType,
    requestedCommissionAmount: applyDefaultAmount(
      requestedType,
      commissionData.requestedCommissionAmount,
    ),
  };

  // Only include current commission if policy period is "current"
  if (isCurrentPolicyPeriod) {
    const currentType = commissionData.currentCommissionType ?? "notApplicable";
    payload.currentCommissionType = currentType;
    payload.currentCommissionAmount = applyDefaultAmount(
      currentType,
      commissionData.currentCommissionAmount,
    );
  }

  return payload;
};

/**
 * Builds a complete commission update request from a single field change
 *
 * When a user edits any commission field in the grid, we need to send ALL commission
 * values to the backend with proper validation:
 * - If policyPeriod is "current": Include both current and requested commission data
 * - If policyPeriod is NOT "current": Include ONLY requested commission data
 * - requestedType is always required by the backend
 *
 * @param benefitPlanId - Benefit plan ID
 * @param fieldChanged - The commission field that was edited
 * @param newValue - The new value for that field
 * @param coverage - The coverage containing the benefit plan
 * @returns Complete commission update with all necessary fields, or null if benefit plan not found
 *
 * @example
 * ```ts
 * const request = buildCommissionUpdateRequest(
 *   "plan-456",
 *   "requestedCommissionAmount",
 *   5,
 *   coverage
 * );
 * if (request) {
 *   await updateCommission({ rfpId, benefitPlanId, ...request });
 * }
 * ```
 */
export const buildCommissionUpdateRequest = (
  benefitPlanId: string,
  fieldChanged: string,
  newValue: unknown,
  coverage: RfpCoverage,
): CommissionPayload | null => {
  // Find the benefit plan
  const benefitPlan = coverage.benefitPlans.find(
    (plan) => plan.identifier === benefitPlanId || plan.id === benefitPlanId,
  );

  if (!benefitPlan) {
    logger.error(
      "[Commission Request Builder] Cannot build commission request - benefit plan not found",
      new Error("Benefit plan not found"),
      { benefitPlanId },
    );
    return null;
  }

  // Extract all current commission values from the benefit plan
  const commissionData = extractCommissionFieldsFromBenefitPlan(benefitPlan);

  // Apply the field change
  const updatedCommissionData = applyFieldChange(
    commissionData,
    fieldChanged,
    newValue,
  );

  // Build payload based on policy period rules
  const payload = buildCommissionPayload(
    updatedCommissionData,
    benefitPlan.policyPeriod || "",
  );

  logger.debug(
    "[Commission Request Builder] Built commission update request:",
    {
      policyPeriod: benefitPlan.policyPeriod,
      fieldChanged,
      payload,
    },
  );

  return payload;
};
